import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Search,
  Layers,
  FileText,
  ShieldCheck,
  MessageCircle,
  RefreshCw,
  ChevronDown,
  Mail,
  Network,
  Presentation,
  BookOpen,
  Scroll,
  LineChart,
  Calculator,
  Compass,
  Swords,
  Shield,
  FileSignature,
  Users,
  Banknote,
  Target,
  MessageSquare,
} from "lucide-react";
import { QorumLogo } from "@/components/brand/QorumLogo";
import {
  AnthropicLogo,
  OpenAILogo,
  GeminiLogo,
  MistralLogo,
  DeepSeekLogo,
  PerplexityLogo,
} from "@/components/brand/llm-logos";
import { FeedbackProductModal, type FeedbackProductInfo } from "@/components/qorum/FeedbackProductModal";
import "@/styles/landing.css";
import "@/styles/comment-ca-marche.css";
import { socialMeta, pageJsonLd } from "@/lib/seo-meta";

export const Route = createFileRoute("/comment-ca-marche")({
  head: () => ({
    meta: [
      { title: "Comment ça marche — Sous le capot du Conseil | Hōjin" },
      {
        name: "description",
        content:
          "Sept IA, agents Groq, recherche temps réel, Trust Score anti-hallucination : l'ingénierie technique de Hōjin, expliquée sans jargon.",
      },
      ...socialMeta({
        title: "Comment ça marche — Sous le capot du Conseil | Hōjin",
        description: "Sept IA, agents Groq, recherche temps réel, Trust Score anti-hallucination : l'ingénierie technique de Hōjin, expliquée sans jargon.",
        url: "https://hojin.ai/comment-ca-marche",
        image: "keyframe",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/comment-ca-marche" }],
    scripts: pageJsonLd({
      path: "/comment-ca-marche",
      name: "Comment ça marche — Sous le capot du Conseil",
      description:
        "Sept IA, agents Groq, recherche temps réel, Trust Score anti-hallucination : l'ingénierie technique de Hōjin, expliquée sans jargon.",
      type: "Article",
      image: "keyframe",
      breadcrumb: [{ name: "Comment ça marche", path: "/comment-ca-marche" }],
    }),
  }),
  component: CommentCaMarchePage,
});


/* ───────────── Hooks ───────────── */

function useInView<T extends HTMLElement>(
  threshold = 0.2,
): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current || seen) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [seen, threshold]);
  return [ref, seen];
}

function Reveal({
  children,
  delay = 0,
  as: As = "div",
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  as?: any;
  className?: string;
}) {
  const [ref, seen] = useInView<HTMLDivElement>();
  return (
    <As
      ref={ref}
      className={`ccm-reveal ${seen ? "ccm-reveal-in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </As>
  );
}

function Counter({ target, durationMs = 1200, suffix = "" }: { target: number; durationMs?: number; suffix?: string }) {
  const [ref, seen] = useInView<HTMLSpanElement>(0.4);
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!seen) return;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(eased * target));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [seen, target, durationMs]);
  return (
    <span ref={ref} className="ccm-counter">
      {val.toLocaleString("fr-FR")}
      {suffix}
    </span>
  );
}

/* ───────────── Data ───────────── */

const COUNCILLORS = [
  { name: "Delphine", role: "La Stratège", model: "Claude Sonnet 4.6 / Opus 4.8", provider: "Anthropic", posture: "Synthèse et vision long terme", accent: "#FF1744", Logo: AnthropicLogo },
  { name: "Henri", role: "Le CFO", model: "GPT-5.4 mini / GPT-5.5", provider: "OpenAI", posture: "Économie, structure, ROI", accent: "#2D5A3D", Logo: OpenAILogo },
  { name: "Akilah", role: "L'Analyste de marché", model: "Gemini 3.5 Flash", provider: "Google", posture: "Cadrage marché, signaux faibles, due diligence", accent: "#5B2A6E", Logo: GeminiLogo },
  { name: "Eva", role: "La Communication", model: "Mistral Small / Large", provider: "Mistral AI · européen", posture: "Marque, perception, narratif", accent: "#FF1744", Logo: MistralLogo },
  { name: "Ren", role: "L'Analyste Devil's Advocate", model: "DeepSeek V4 Flash", provider: "DeepSeek", posture: "Contradicteur permanent, dissidence motivée", accent: "#4A4F58", Logo: DeepSeekLogo },
  { name: "Julien", role: "Les Opérations", model: "Sonar / Sonar Pro", provider: "Perplexity", posture: "Faisabilité, exécution, données en temps réel", accent: "#1A2333", Logo: PerplexityLogo },
];

const MODES = [
  "Mode Détection automatique",
  "Mode Validation d'hypothèse",
  "Mode Étude de marché",
  "Mode Pricing",
  "Mode Lancement produit",
  "Mode Levée de fonds",
  "Mode Plan stratégique",
  "Mode Tendances sectorielles",
];

const GROQ_AGENTS = [
  { name: "GREFFE DES CONTRADICTIONS", mission: "PV de séance, proof_layer" },
  { name: "RECHERCHE WEB", mission: "Tavily temps réel" },
  { name: "APPROFONDISSEMENT", mission: "Axes faibles du débat" },
  { name: "DUE DILIGENCE INVERSÉE", mission: "Angles investisseur" },
  { name: "PRÉPARATION LIVRABLES", mission: "Documents Phase Pro" },
  { name: "PRÉPARATION ACTIONS", mission: "Phase Exécutive — bientôt" },
];

const RESEARCH_FEED = [
  { src: "Reuters", title: "EU adopts AI Act sectoral guidelines", snippet: "Les fournisseurs de modèles fondationnels devront publier un résumé…" },
  { src: "McKinsey", title: "The state of AI in early 2026", snippet: "70% des entreprises ont déployé au moins une fonction IA en production…" },
  { src: "Bloomberg", title: "Cloud infrastructure pricing Q1 2026", snippet: "Les LPU Groq tarifés 0,27 USD par million de tokens en entrée…" },
  { src: "CB Insights", title: "Vertical SaaS funding tracker", snippet: "Les tours Series A dans le vertical legal-tech ont chuté de 18%…" },
  { src: "Crunchbase", title: "Founders demographics 2025", snippet: "62% des founders pre-seed sont solo lors du premier tour…" },
];

const TRUST_STAGES = [
  { icon: Search, title: "Étage 1 — Recherche obligatoire", text: "Aucun Conseiller ne peut citer un fait sans qu'une source ait été récupérée. Pas de source, pas d'affirmation chiffrée." },
  { icon: Layers, title: "Étage 2 — Rerank Cohere multilingual-v3.0", text: "Les sources passent par Cohere rerank multilingual-v3.0, qui classe par pertinence sémantique (FR/EN/ES/DE). Les moins pertinentes sont éliminées avant l'arrivée au Conseil." },
  { icon: FileText, title: "Étage 3 — Documentation des contradictions", text: "Pendant le débat, Hōjin trace chaque désaccord interne au Conseil. Si Delphine et Henri se contredisent sur un chiffre, c'est enregistré et présenté au founder." },
  { icon: ShieldCheck, title: "Étage 4 — Audit indépendant Claude Haiku 4.5", text: "Avant publication, Hōjin relit le verdict via un module d'audit indépendant (Claude Haiku 4.5) et attribue un indicateur de confiance par section : 🟢 source unanime, 🟡 contesté, 🔴 incertain." },
];

type ProCard = {
  key: string;
  name: string;
  Icon: typeof FileText;
  priceLabel: string;
  defaultPrice: number;
  pitch: string;
  description: string;
  status: "soon" | "soon";
  ctaHref?: string;
  ctaLabel?: string;
};

type ExecCard = {
  key: string;
  name: string;
  Icon: typeof FileText;
  priceLabel: string;
  defaultPrice: number;
  pitch: string;
  v1?: string;
  v2: string;
  note?: string;
  badge: "soon" | "soon";
};

const PRO_CARDS: ProCard[] = [
  { key: "memo", name: "Memo exécutif", Icon: FileText, priceLabel: "29 €", defaultPrice: 29,
    pitch: "Synthèse 1-2 pages style cabinet conseil.",
    description: "Verdict + décisions critiques + risques + dissidence + sources. Format markdown ou HTML.",
    status: "soon" },
  { key: "pitch_deck", name: "Pitch deck investisseurs", Icon: Presentation, priceLabel: "49 €", defaultPrice: 49,
    pitch: "12-15 slides clés pour pitcher.",
    description: "Problème, solution, marché, traction, équipe, demande. Format PowerPoint + Google Slides + Keynote.",
    status: "soon" },
  { key: "business_plan", name: "Business plan complet", Icon: BookOpen, priceLabel: "149 €", defaultPrice: 149,
    pitch: "50-80 pages investissable.",
    description: "Marché, modèle, roadmap, équipe, finances projetées sur 3 ans. Word et PDF, prêt série A.",
    status: "soon" },
  { key: "white_paper", name: "White paper sectoriel", Icon: Scroll, priceLabel: "79 €", defaultPrice: 79,
    pitch: "15-30 pages — étude de fond positionnante.",
    description: "Analyse approfondie d'un sujet pour bâtir l'autorité d'un founder sur un domaine.",
    status: "soon" },
  { key: "market_study", name: "Étude de marché", Icon: LineChart, priceLabel: "99 €", defaultPrice: 99,
    pitch: "20-40 pages — cartographie sourcée.",
    description: "TAM/SAM/SOM, concurrents, segments, signaux faibles. Tavily + Perplexity Sonar, Cohere multilingual.",
    status: "soon" },
  { key: "competitive_study", name: "Étude concurrentielle", Icon: Swords, priceLabel: "69 €", defaultPrice: 69,
    pitch: "15-25 pages — face à face concurrentiel.",
    description: "Cartographie des concurrents directs et indirects, comparaison pricing/positioning, opportunités sous-servies.",
    status: "soon" },
  { key: "financial_plan", name: "Plan financier 3 ans", Icon: Calculator, priceLabel: "79 €", defaultPrice: 79,
    pitch: "P&L + cash flow Excel + DCF.",
    description: "Modèle 3-statement sur 3 ans + DCF + sensibilités. Excel modifiable, hypothèses tracées.",
    status: "soon" },
  { key: "strategic_report", name: "Rapport stratégique", Icon: Compass, priceLabel: "49 €", defaultPrice: 49,
    pitch: "10-15 pages — synthèse stratégique.",
    description: "Diagnostic + options + recommandation chiffrée. Pour les décisions de portefeuille, M&A, pivot stratégique.",
    status: "soon" },
];

const EXEC_CARDS: ExecCard[] = [
  { key: "mails", name: "Mails", Icon: Mail, priceLabel: "4,99 € – 69 €", defaultPrice: 5,
    pitch: "Drafts préparés, vous validez, vous envoyez.",
    v1: "Mail prospect 4,99 € · Mail invest 9,99 € · Packs 39-69 €",
    v2: "10+ types : suivi devis, relance B2B, négociation salaire, onboarding.",
    badge: "soon" },
  { key: "contrats", name: "Contrats juridiques", Icon: FileSignature, priceLabel: "9,99 € – 199 €", defaultPrice: 29,
    pitch: "Templates adaptés à votre secteur.",
    v1: "NDA FR+EN 9,99 € · CGV 29 €",
    v2: "Pacte d'associés 79 €, Pack SAS complet 199 €, Contrat travail 39 €, Mandat social 39 €",
    note: "Partenariat Maître Abel Sabeur (Legal First)",
    badge: "soon" },
  { key: "admin_ip", name: "Administrations & IP", Icon: Shield, priceLabel: "49 € – 149 €", defaultPrice: 49,
    pitch: "Protégez votre marque, captez les aides.",
    v1: "INPI marque assisté 49 €",
    v2: "Enveloppe Soleau 29 €, Brevet pré-analyse 79 €, Dossier JEI 99 €, CIR 99 €, Prêt amorçage 149 €, URSSAF 49 €",
    note: "API INPI publique (gratuite)",
    badge: "soon" },
  { key: "recrutement", name: "Recrutement & RH", Icon: Users, priceLabel: "19 € – 79 €", defaultPrice: 29,
    pitch: "Du brief à l'onboarding.",
    v2: "Fiche de poste 19 €, Annonce LinkedIn/WTTJ 29 €, Plan recrutement 6 mois 49 €, Grille salaires 79 €, Process entretien 49 €",
    badge: "soon" },
  { key: "financement", name: "Financement & investisseurs", Icon: Banknote, priceLabel: "49 € – 99 €", defaultPrice: 49,
    pitch: "Cartographie & préparation levée.",
    v2: "Liste 20 VC ciblés 49 €, Liste 20 BA français 49 €, Data room pré-organisée 79 €, Plan levée 6 mois 99 €",
    badge: "soon" },
  { key: "commercial", name: "Commercial", Icon: Target, priceLabel: "49 € – 449 €", defaultPrice: 49,
    pitch: "Process et plans de conquête.",
    v2: "Process BANT/MEDDIC 49 €, Plan Social Selling LinkedIn B2B 299 €, Plan Instagram/TikTok B2C 299 €, Plan Hybride 449 €",
    badge: "soon" },
  { key: "social_selling", name: "Social Selling", Icon: MessageSquare, priceLabel: "19 € – 149 €", defaultPrice: 49,
    pitch: "Contenus pour fonder votre autorité.",
    v2: "Pack 30 posts LinkedIn founder 99 €, Pack 30 tweets 49 €, Pack 10 carrousels 79 €, Calendrier éditorial 90j 149 €, Scripts TikTok viraux 99 €, Scripts Reels 99 €, Bio Instagram + Linktree 19 €, Hashtag strategy 39 €",
    badge: "soon" },
  { key: "reseau_qorum", name: "Réseau Hōjin", Icon: Network, priceLabel: "99 €+", defaultPrice: 99,
    pitch: "Le bon contact, au bon moment.",
    v2: "Warm intro Maître Abel Sabeur 99 € · Recommandations comptable/banque/SaaS (gratuit, sur commission).",
    note: "Stratégie hybride : vos contacts (priorité) → Réseau Hōjin (opt-in) → Identification publique (LinkedIn/Crunchbase).",
    badge: "soon" },
];

/* ───────────── Components ───────────── */

function Kicker({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <div className={`ccm-kicker ${className}`}>{children}</div>;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="ccm-h2">{children}</h2>;
}

function Check({ ok }: { ok: boolean }) {
  return (
    <span className={ok ? "ccm-check ccm-check-ok" : "ccm-check ccm-check-no"} aria-label={ok ? "oui" : "non"}>
      {ok ? "✓" : "✗"}
    </span>
  );
}

function GroqTimeline() {
  const [ref, seen] = useInView<HTMLDivElement>(0.3);
  return (
    <div ref={ref} className="ccm-groq-timeline" aria-hidden="true">
      <div className="ccm-groq-grid">
        {GROQ_AGENTS.map((a, i) => (
          <div key={a.name} className="ccm-groq-track">
            <div className="ccm-groq-rail" />
            {seen && (
              <div
                className="ccm-groq-ball"
                style={{
                  animationDuration: `${1.2 + i * 0.2}s`,
                  animationDelay: `${i * 0.08}s`,
                }}
              />
            )}
            <div className="ccm-groq-label">
              <strong>{a.name}</strong>
              <span>{a.mission}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmileChat() {
  const messages = [
    { from: "emile", text: "Bonjour. Avant que le Conseil ne siège, j'aimerais clarifier votre intention. Quelle décision cherchez-vous à prendre cette semaine ?" },
    { from: "user", text: "Faut-il que je lève maintenant ou dans 6 mois ?" },
    { from: "emile", text: "Et qu'est-ce qui rendrait, selon vous, le moment actuel défavorable ?" },
    { from: "user", text: "La traction est encore fragile, mais le marché bouge vite." },
    { from: "emile", text: "Très bien. Le Conseil aura besoin de chiffrer cette fragilité. Souhaitez-vous le mode « Levée de fonds » ?" },
  ];
  const [ref, seen] = useInView<HTMLDivElement>(0.3);
  return (
    <div ref={ref} className="ccm-chat">
      {messages.map((m, i) => (
        <div
          key={i}
          className={`ccm-bubble ccm-bubble-${m.from} ${seen ? "ccm-bubble-in" : ""}`}
          style={{ animationDelay: `${i * 700}ms` }}
        >
          {m.text}
        </div>
      ))}
    </div>
  );
}

function ResearchMockup() {
  const [ref, seen] = useInView<HTMLDivElement>(0.3);
  return (
    <div ref={ref} className="ccm-research">
      <div className="ccm-research-head">
        <span className="ccm-pulse" aria-hidden /> {seen ? "5 recherches en cours…" : "Connexion…"}
      </div>
      <div className="ccm-research-list">
        {RESEARCH_FEED.map((r, i) => (
          <div
            key={i}
            className={`ccm-research-card ${seen ? "ccm-glow-in" : ""}`}
            style={{ animationDelay: `${i * 600}ms` }}
          >
            <div className="ccm-research-src">{r.src}</div>
            <div className="ccm-research-title">{r.title}</div>
            <div className="ccm-research-snippet">{r.snippet}</div>
          </div>
        ))}
      </div>
      <div className="ccm-research-foot">Sources vérifiées par Perplexity Sonar + Tavily</div>
    </div>
  );
}


function StabiloDemo() {
  return (
    <div className="ccm-stabilo-demo" role="img" aria-label="Démonstration animée du Dialogue Hōjin : sélection d'un passage du verdict, barre contextuelle 4 actions, fenêtre Dialogue avec réponse vérifiée">
      <div className="ccm-stabilo-mockup">
        <div className="ccm-stabilo-doc">
          <div className="ccm-stabilo-doc-kicker">§ VERDICT — RECOMMANDATION HENRI</div>
          <p className="ccm-stabilo-doc-text">
            Le Conseil recommande d'avancer le lancement de 6 semaines. Henri (CFO) note que
            <span className="ccm-stabilo-target">
              <span className="ccm-stabilo-hl"> le burn rate actuel permet une fenêtre de 9 mois </span>
              <span className="ccm-stabilo-toolbar" aria-hidden="true">
                <button type="button" className="ccm-stabilo-action" tabIndex={-1}>Approfondir</button>
                <button type="button" className="ccm-stabilo-action" tabIndex={-1}>Expliquer</button>
                <button type="button" className="ccm-stabilo-action ccm-stabilo-action-active" tabIndex={-1}>Vérifier</button>
                <button type="button" className="ccm-stabilo-action" tabIndex={-1}>Non pertinent</button>
              </span>
            </span>
            avant tout besoin de refinancement. Akilah (Analyste de marché) souligne le signal faible détecté sur le segment B2B européen visé par le pivot.
          </p>
        </div>

        <div className="ccm-stabilo-panel" aria-hidden="true">
          <div className="ccm-stabilo-panel-head">
            <MessageCircle size={14} aria-hidden /> Dialogue Hōjin — Vérification d'une assertion
            <span className="ccm-stabilo-trust" title="Trust Score vert" />
          </div>
          <div className="ccm-stabilo-panel-quote">
            Vous avez sélectionné dans « Recommandation Henri » :<br />
            <em>« le burn rate actuel permet une fenêtre de 9 mois »</em>
          </div>
          <div className="ccm-stabilo-panel-answer">
            <span className="ccm-stabilo-type">
              Cette estimation repose sur 3 hypothèses vérifiables. Henri a chiffré le burn à 87&nbsp;K€/mois sur la base des relevés Q1 2026 transmis…
            </span>
          </div>
        </div>
      </div>

      <div className="ccm-stabilo-bar" aria-hidden="true">
        4 actions disponibles depuis n'importe quel passage du verdict&nbsp;:
        <span> [Approfondir] · [Expliquer] · [Vérifier] · [Non pertinent]</span>
      </div>
      <p className="ccm-stabilo-caption">
        Le Dialogue Hōjin en action. Sélectionnez n'importe quel passage du verdict réel et obtenez en quelques secondes une vérification, une explication, un approfondissement — ou rejetez l'assertion comme non pertinente.
      </p>
    </div>
  );
}

/* ───────────── Page ───────────── */

function CommentCaMarchePage() {
  const [feedback, setFeedback] = useState<FeedbackProductInfo | null>(null);
  return (
    <div className="ql-root ccm-root" data-testid="ccm-root">
      {feedback ? (
        <FeedbackProductModal product={feedback} onClose={() => setFeedback(null)} />
      ) : null}
      <header className="ql-topbar">
        <div className="ql-wrap ql-topbar-inner">
          <QorumLogo size="sm" as="a" href="/" ariaLabel="Hōjin — accueil" />
          <nav className="ql-topbar-nav">
            <Link to="/" style={{ color: "var(--charcoal)" }}>← Retour</Link>
            <Link to="/phase-pro">La Phase Pro</Link>
            <Link to="/signup" className="ql-topbar-cta">Saisir le Conseil</Link>
          </nav>
        </div>
      </header>

      {/* SECTION 0 — HERO TECH */}
      <section className="ccm-hero" aria-labelledby="ccm-title">
        <div className="ql-wrap ccm-hero-inner">
          <Kicker>§ SOUS LE CAPOT</Kicker>
          <h1 id="ccm-title" className="ccm-h1">
            Hōjin
            {" "}— six IA délibèrent. <em>Vraiment.</em>
          </h1>
          <p className="ccm-sub" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: "italic", color: "var(--gold-deep)", marginTop: 4, fontSize: 22 }}>
            The Supervisor — Conseil de Surveillance des IA.
          </p>
          <p className="ccm-sub">L'ingénierie technique derrière Hōjin — pour les fondateurs qui veulent savoir ce qu'ils achètent.</p>
          <p className="ccm-lead">
            Ce que vous voyez sur l'écran — un débat entre six IA distinctes — repose sur une architecture
            qu'aucun outil concurrent ne possède : six modèles de pointe orchestrés en parallèle, augmentés
            par une équipe d'enquête sur Groq, une recherche temps réel multi-sources, et un protocole
            anti-hallucination industriel. Nous expliquons tout, sans jargon.
          </p>
          <a href="#conseil-six" className="ccm-scroll">
            <span>Découvrir</span>
            <ChevronDown size={20} aria-hidden />
          </a>
        </div>
      </section>

      {/* SECTION 1 — LE CONSEIL DES SIX */}
      <section className="ccm-section ccm-bg-beige" id="conseil-six" aria-labelledby="ccm-s1">
        <div className="ql-wrap">
          <Kicker>§ I. LE CONSEIL</Kicker>
          <SectionTitle><span id="ccm-s1">Six modèles distincts, jamais un seul.</span></SectionTitle>
          <p className="ccm-p">
            Quand vous posez une question stratégique, six IA différentes — pas six instances du même modèle
            — siègent au Conseil. Chacune a son tempérament, ses sources d'entraînement, son style d'argumentation.
            Le débat émerge de leurs désaccords. Le verdict émerge de leur convergence.
          </p>

          <div className="ccm-council-grid" data-testid="ccm-council-grid">
            {COUNCILLORS.map((c, i) => (
              <Reveal key={c.name} delay={i * 80}>
                <article className="ccm-council-card" style={{ "--accent": c.accent } as React.CSSProperties}>
                  <div className="ccm-council-portrait" aria-hidden>
                    <c.Logo width={28} height={28} />
                  </div>
                  <div className="ccm-council-meta">
                    <h3>{c.name}</h3>
                    <div className="ccm-council-role">{c.role}</div>
                    <div className="ccm-council-model">{c.provider}</div>
                    <p className="ccm-council-posture">{c.posture}</p>
                  </div>
                  <div className="ccm-council-stripe" aria-hidden />
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="ccm-table-wrap" data-testid="ccm-table-council">
              <h3 className="ccm-table-h">Pourquoi 6 modèles ≠ 1 modèle multi-prompt</h3>
              <table className="ccm-table">
                <thead>
                  <tr>
                    <th>Critère</th><th>Hōjin</th><th>ChatGPT</th><th>Perplexity</th><th>Claude</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Nombre de modèles distincts</td><td>6</td><td>1</td><td>1</td><td>1</td></tr>
                  <tr><td>Désaccord documenté</td><td><Check ok /></td><td><Check ok={false} /></td><td><Check ok={false} /></td><td><Check ok={false} /></td></tr>
                  <tr><td>Note de dissidence formelle</td><td><Check ok /></td><td><Check ok={false} /></td><td><Check ok={false} /></td><td><Check ok={false} /></td></tr>
                  <tr><td>Contradictions documentées</td><td><Check ok /></td><td><Check ok={false} /></td><td><Check ok={false} /></td><td><Check ok={false} /></td></tr>
                  <tr><td>Verdict par consensus</td><td><Check ok /></td><td><Check ok={false} /></td><td><Check ok={false} /></td><td><Check ok={false} /></td></tr>
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 2 — ANTICHAMBRE */}
      <section className="ccm-section" aria-labelledby="ccm-s2">
        <div className="ql-wrap">
          <Kicker>§ II. L'ANTICHAMBRE</Kicker>
          <SectionTitle><span id="ccm-s2">Avant le Conseil, l'écoute.</span></SectionTitle>
          <p className="ccm-p">
            Hōjin vous reçoit dans l'Antichambre. Il ne donne pas de réponse. Il pose les bonnes questions.
            À la manière socratique, Hōjin clarifie votre intention en trois à sept tours. Sans dériver.
            Sans jamais sortir de votre sujet.
          </p>
          <div className="ccm-split">
            <Reveal><EmileChat /></Reveal>
            <Reveal delay={120}>
              <div className="ccm-modes">
                <h3 className="ccm-modes-h">8 modes calibrés.</h3>
                <ul className="ccm-modes-list">
                  {MODES.map((m) => (
                    <li key={m}><span className="ccm-bullet" aria-hidden />{m}</li>
                  ))}
                </ul>
                <p className="ccm-modes-note">
                  Chaque mode possède son propre prompt-système calibré contre la dérive
                  (anti-drift P0.1 vérifié).
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 3 — ÉQUIPE GROQ */}
      <section className="ccm-section ccm-bg-beige" aria-labelledby="ccm-s3">
        <div className="ql-wrap">
          <Kicker>§ III. L'ÉQUIPE D'ENQUÊTE</Kicker>
          <SectionTitle><span id="ccm-s3">Six recherches en parallèle, sur Groq.</span></SectionTitle>
          <p className="ccm-p">
            Pendant que les Conseillers débattent, Hōjin enquête en parallèle sur Groq —
            l'infrastructure d'inférence la plus rapide du monde. Six recherches spécialisées en moins
            de deux secondes.
          </p>

          <Reveal><GroqTimeline /></Reveal>

          <Reveal>
            <p className="ccm-p ccm-p-tight">
              Groq utilise des LPU (Language Processing Units) à très faible latence, ce qui permet
              d'exécuter ces six recherches en parallèle sans ralentir l'expérience. Sur GPU classique,
              le même travail prendrait 8-12 secondes en série.
            </p>
          </Reveal>

          <div className="ccm-counter-wrap" data-testid="ccm-counter-groq">
            <Counter target={1200} suffix=" ms" />
            <div className="ccm-counter-cap">pour 6 enquêtes parallèles, sur Groq.</div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — RECHERCHE TEMPS RÉEL */}
      <section className="ccm-section" aria-labelledby="ccm-s4">
        <div className="ql-wrap">
          <Kicker>§ IV. LA RECHERCHE</Kicker>
          <SectionTitle><span id="ccm-s4">Citations vérifiables, en temps réel.</span></SectionTitle>
          <p className="ccm-p">
            Aucun verdict n'est rendu sans sources. Hōjin interroge Tavily et Perplexity Sonar en temps réel
            pendant la délibération, sans s'arrêter tant qu'il n'a pas trouvé de citations fiables.
            Chaque chiffre, chaque affirmation reçoit son URL.
          </p>

          <Reveal><ResearchMockup /></Reveal>

          <Reveal>
            <div className="ccm-table-wrap">
              <h3 className="ccm-table-h">Sources accessibles</h3>
              <table className="ccm-table">
                <thead>
                  <tr><th>Source</th><th>Couverture</th><th>Latence</th></tr>
                </thead>
                <tbody>
                  <tr><td>Tavily</td><td>Web ouvert + news</td><td>&lt; 800 ms</td></tr>
                  <tr><td>Perplexity Sonar</td><td>Synthèse + citations</td><td>&lt; 2 s</td></tr>
                  <tr><td>Total moyen par enquête</td><td>5-8 sources vérifiées</td><td>&lt; 3 s</td></tr>
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 5 — TRUST SCORE */}
      <section className="ccm-section ccm-bg-gradient" aria-labelledby="ccm-s5">
        <div className="ql-wrap">
          <Kicker>§ V. LE CONSEIL DE SURVEILLANCE</Kicker>
          <SectionTitle><span id="ccm-s5">Le Conseil de Surveillance — protocole anti-hallucination.</span></SectionTitle>
          <p className="ccm-p">
            Le risque numéro un des IA génératives, c'est l'hallucination plausible : une affirmation fausse
            mais bien tournée. Hōjin déploie un protocole industriel pour le neutraliser, à plusieurs étages.
          </p>

          <div className="ccm-trust-grid">
            {TRUST_STAGES.map((s, i) => (
              <Reveal key={s.title} delay={i * 100}>
                <article className="ccm-trust-card">
                  <div className="ccm-trust-icon" aria-hidden><s.icon size={22} /></div>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="ccm-table-wrap" data-testid="ccm-table-trust">
              <h3 className="ccm-table-h">Comparatif protocole anti-hallucination</h3>
              <table className="ccm-table">
                <thead>
                  <tr>
                    <th>Mécanisme</th><th>Hōjin</th><th>ChatGPT Pro</th><th>Perplexity Pro</th><th>Claude.ai</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Recherche temps réel</td><td>Tavily + Sonar</td><td>Bing</td><td>Native</td><td>Native</td></tr>
                  <tr><td>Rerank multilingual</td><td><Check ok /> Cohere v3</td><td><Check ok={false} /></td><td>partiel</td><td><Check ok={false} /></td></tr>
                  <tr><td>Contradictions documentées</td><td><Check ok /></td><td><Check ok={false} /></td><td><Check ok={false} /></td><td><Check ok={false} /></td></tr>
                  <tr><td>Audit indépendant</td><td><Check ok /> Haiku 4.5</td><td><Check ok={false} /></td><td><Check ok={false} /></td><td><Check ok={false} /></td></tr>
                  <tr><td>Confiance par section</td><td><Check ok /> 3 niveaux</td><td><Check ok={false} /></td><td><Check ok={false} /></td><td><Check ok={false} /></td></tr>
                  <tr><td>Dissidence formelle</td><td><Check ok /> Ren</td><td><Check ok={false} /></td><td><Check ok={false} /></td><td><Check ok={false} /></td></tr>
                  <tr><td>Audit trail exportable</td><td><Check ok /></td><td><Check ok={false} /></td><td><Check ok={false} /></td><td><Check ok={false} /></td></tr>
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 6 — CONSEIL ITÉRATIF */}
      <section className="ccm-section ccm-bg-beige" aria-labelledby="ccm-s6">
        <div className="ql-wrap">
          <Kicker>§ VI. LA CONVERSATION</Kicker>
          <SectionTitle><span id="ccm-s6">Hōjin reste à votre service.</span></SectionTitle>
          <p className="ccm-p">
            Un verdict ne clôt pas le débat. Vous gardez deux moyens de continuer à interroger Hōjin :
          </p>

          <Reveal>
            <StabiloDemo />
          </Reveal>

          <div className="ccm-conv-grid">

            <Reveal>
              <article className="ccm-conv-card">
                <div className="ccm-conv-icon" aria-hidden><MessageCircle size={22} /></div>
                <h3>Le Dialogue Hōjin</h3>
                <p>
                  Pour les clarifications rapides : vous discutez avec Hōjin qui a relu toute la délibération.
                  Réponse en quelques secondes. Inclus dans chaque saisine.
                </p>
                <span className="ccm-badge ccm-badge-ok">Inclus</span>
              </article>
            </Reveal>
            <Reveal delay={120}>
              <article className="ccm-conv-card">
                <div className="ccm-conv-icon" aria-hidden><RefreshCw size={22} /></div>
                <h3>Relancer un débat complet</h3>
                <p>
                  Pour les vraies nouvelles questions stratégiques : les six Conseillers siègent à nouveau,
                  en repartant du verdict précédent. Nouveau verdict, nouvelle dissidence, nouvelles sources.
                  Chaînage v1 → v2 → v3.
                </p>
                <span className="ccm-badge">À la demande</span>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 7 — LE FOYER */}
      <section className="ccm-section ccm-foyer" id="foyer" aria-labelledby="ccm-s7">
        <div className="ql-wrap">
          <div className="ccm-foyer-intro">
            <Kicker>§ VII. LE FOYER</Kicker>
            <h2 className="ccm-h2 ccm-foyer-h" id="ccm-s7">Le Foyer — votre cabinet privé.</h2>
            <p className="ccm-sub ccm-foyer-sub">
              Une fois le verdict rendu, vous entrez au Foyer. Trois portes s'ouvrent : la conversation,
              la documentation, l'exécution. Vous restez maître à chaque étape.
            </p>
            <p className="ccm-p ccm-foyer-p">
              Hōjin n'est pas un outil de décision unique. Une fois la délibération terminée, le Foyer prolonge
              la relation avec votre Conseil. Vous pouvez poursuivre le Dialogue Hōjin, demander à Hōjin
              de produire des livrables documentaires en Phase Pro, ou solliciter Hōjin (mode Exécution)
              pour préparer vos actions concrètes — du dépôt INPI à la mise en relation avec votre prochain
              investisseur.
            </p>
          </div>

          {/* Schéma d'architecture */}
          <div className="ccm-foyer-schema" data-testid="ccm-foyer-schema" aria-hidden="true">
            <Reveal>
              <div className="ccm-foyer-root-node">LE FOYER</div>
            </Reveal>
            <div className="ccm-foyer-trunk" />
            <div className="ccm-foyer-branches">
              {[
                { id: "foyer-dialogue", label: "Le Dialogue", sub: "conversation" },
                { id: "foyer-phase-pro", label: "La Phase Pro", sub: "documentation" },
                { id: "foyer-phase-exec", label: "La Phase Exécutive", sub: "action" },
              ].map((b, i) => (
                <Reveal key={b.id} delay={100 + i * 100}>
                  <a href={`#${b.id}`} className="ccm-foyer-branch">
                    <span className="ccm-foyer-branch-name">{b.label}</span>
                    <span className="ccm-foyer-branch-sub">{b.sub}</span>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>

          {/* 7.1 — Le Dialogue */}
          <div className="ccm-foyer-sub" id="foyer-dialogue">
            <div className="ccm-foyer-sub-grid">
              <div>
                <div className="ccm-foyer-kicker">7.1 · LE DIALOGUE</div>
                <h3 className="ccm-foyer-sub-h">Le Dialogue Hōjin — votre porte-parole.</h3>
                <p className="ccm-p">
                  Le verdict rendu n'a pas vidé le Conseil. Hōjin reste à votre écoute — un porte-parole
                  conversationnel qui a relu chaque acte, chaque source, chaque contradiction. Pour clarifier,
                  approfondir, vérifier, ou rejeter un point précis.
                </p>
                <ul className="ccm-foyer-actions">
                  <li><span className="ccm-bullet" aria-hidden /><strong>Approfondir</strong> — détailler une section du verdict</li>
                  <li><span className="ccm-bullet" aria-hidden /><strong>Expliquer</strong> — reformuler dans vos termes</li>
                  <li><span className="ccm-bullet" aria-hidden /><strong>Vérifier</strong> — re-sourcer une assertion</li>
                  <li><span className="ccm-bullet" aria-hidden /><strong>Non pertinent</strong> — marquer un point à écarter</li>
                </ul>
                <blockquote className="ccm-foyer-quote">
                  « Vous restez au volant. Le Dialogue Hōjin éclaire, il ne tranche pas. »
                </blockquote>
              </div>
              <div>
                <Reveal><StabiloDemo /></Reveal>
              </div>
            </div>
          </div>

          {/* 7.2 — La Phase Pro */}
          <div className="ccm-foyer-sub" id="foyer-phase-pro">
            <div className="ccm-foyer-kicker">7.2 · LA PHASE PRO</div>
            <h3 className="ccm-foyer-sub-h ccm-foyer-sub-h-center">La Phase Pro — huit livrables documentaires.</h3>
            <p className="ccm-p ccm-foyer-p-center">
              Hōjin transforme la délibération en documents exploitables. Chaque livrable est sourcé,
              structuré, prêt à transmettre ou à imprimer. Trois sont disponibles dès la phase bêta, cinq autres
              rejoignent le catalogue après le lancement public.
            </p>
            <div className="ccm-foyer-grid" data-testid="ccm-foyer-pro-grid">
              {PRO_CARDS.map((c, i) => (
                <Reveal key={c.key} delay={i * 60}>
                  <article className="ccm-foyer-card" data-testid={`foyer-pro-${c.key}`}>
                    <div className="ccm-foyer-card-head">
                      <c.Icon size={28} className="ccm-foyer-card-icon" aria-hidden />
                      <span className="ccm-foyer-badge ccm-foyer-badge-soon">Bientôt</span>
                    </div>
                    <div className="ccm-foyer-card-title-row">
                      <h4 className="ccm-foyer-card-h">{c.name}</h4>
                      <aside className="ccm-foyer-card-price">{c.priceLabel}</aside>
                    </div>
                    <p className="ccm-foyer-card-pitch">{c.pitch}</p>
                    <p className="ccm-foyer-card-desc">{c.description}</p>
                    <button
                      type="button"
                      className="ccm-foyer-cta ccm-foyer-cta-outline"
                      data-testid={`foyer-cta-${c.key}`}
                      onClick={() => setFeedback({ key: c.key, name: c.name, defaultPrice: c.defaultPrice })}
                    >
                      Donner mon avis →
                    </button>
                  </article>
                </Reveal>
              ))}
            </div>
            <p className="ccm-foyer-note">
              Pricing affiché à titre indicatif. Les tarifs finaux seront fixés selon les retours bêta. Plan Pro
              49&nbsp;€/mois inclut 1 livrable Phase Pro par mois au choix.
            </p>
          </div>

          {/* 7.3 — La Phase Exécutive */}
          <div className="ccm-foyer-sub" id="foyer-phase-exec">
            <div className="ccm-foyer-kicker">7.3 · LA PHASE EXÉCUTIVE</div>
            <h3 className="ccm-foyer-sub-h ccm-foyer-sub-h-center">La Phase Exécutive — huit catégories d'action.</h3>
            <p className="ccm-p ccm-foyer-p-center">
              Hōjin (mode Exécution) prend le relais. Là où Hōjin DOCUMENTE en Phase Pro, ici Hōjin
              PRÉPARE l'action. Huit catégories couvrent les besoins concrets d'un fondateur français
              ou européen — de la protection de votre marque à votre prochaine rencontre investisseur.
              Hébergé sur un site dédié.
            </p>
            <blockquote className="ccm-foyer-quote ccm-foyer-quote-center">
              « Hōjin ne décide jamais à votre place. Hōjin prépare, vous validez, l'action part. L'IA augmente
              le jugement humain, elle ne le remplace pas. »
            </blockquote>
            <div className="ccm-foyer-grid" data-testid="ccm-foyer-exec-grid">
              {EXEC_CARDS.map((c, i) => (
                <Reveal key={c.key} delay={i * 60}>
                  <article className="ccm-foyer-card" data-testid={`foyer-exec-${c.key}`}>
                    <div className="ccm-foyer-card-head">
                      <c.Icon size={28} className="ccm-foyer-card-icon" aria-hidden />
                      <span className="ccm-foyer-badge ccm-foyer-badge-soon">Bientôt</span>
                    </div>
                    <div className="ccm-foyer-card-title-row">
                      <h4 className="ccm-foyer-card-h">{c.name}</h4>
                      <aside className="ccm-foyer-card-price">{c.priceLabel}</aside>
                    </div>
                    <p className="ccm-foyer-card-pitch">{c.pitch}</p>
                    {c.v1 ? (
                      <p className="ccm-foyer-card-desc"><strong>V1 :</strong> {c.v1}</p>
                    ) : null}
                    <p className="ccm-foyer-card-desc"><strong>V2 :</strong> {c.v2}</p>
                    {c.note ? (
                      <p className="ccm-foyer-card-note"><em>{c.note}</em></p>
                    ) : null}
                    <button
                      type="button"
                      className="ccm-foyer-cta ccm-foyer-cta-outline"
                      data-testid={`foyer-cta-${c.key}`}
                      onClick={() => setFeedback({ key: c.key, name: c.name, defaultPrice: c.defaultPrice })}
                    >
                      Donner mon avis →
                    </button>
                  </article>
                </Reveal>
              ))}
            </div>

            {/* Bandeau final executives.qorum.expert */}
            <Reveal>
              <div className="ccm-foyer-exec-banner" data-testid="ccm-foyer-exec-banner">
                <p className="ccm-foyer-exec-banner-text">
                  La Phase Exécutive est hébergée sur un site dédié, avec sa propre direction artistique
                  — « Mad Men office, 1970s » : utility, pragmatique, brun chocolat, moutarde, burnt orange.
                </p>
                <a
                  href="https://executives.qorum.expert"
                  className="ccm-foyer-exec-cta"
                  data-testid="ccm-foyer-exec-cta"
                  rel="noopener"
                >
                  Passons à l'exécution →
                </a>
                <p className="ccm-foyer-exec-banner-note">
                  Hōjin Executives ouvre en novembre 2026. En attendant, le lien redirige vers la page d'attente.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>




      {/* SECTION 8 — CTA FINAL */}
      <section className="ccm-section ccm-cta-final">
        <div className="ql-wrap" style={{ textAlign: "center" }}>
          <h2 className="ccm-h2">Soumettez la première question à Hōjin.</h2>
          <p className="ccm-sub">Trente secondes pour saisir, deux à trois minutes pour le verdict. Le Foyer s'ouvre ensuite.</p>
          <div className="ccm-cta-row" style={{ justifyContent: "center" }}>
            <Link to="/" className="ccm-cta-primary" data-testid="ccm-cta-final">Convoquer le Conseil →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// Wrapper component used by Kicker that accepts className prop
// (TS-friendly; defined inline as a higher-order workaround)
function _kickerWrapper() {}
void _kickerWrapper;
